import React, { useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import productData from "../data/products.json";
import RelatedProduct from "./RelatedProduct";
import toast from "react-hot-toast";

function ShopDetailsMain() {
  const [activeTab, setActiveTab] = useState("tab-desc");
  const [quantity, setQuantity] = useState(1);

  const { id } = useParams();
  const navigate = useNavigate();

  const product = productData.find((p) => p.id === parseInt(id, 10));

  // 🔹 Random reviews (always defined, hooks at top)
  const randomReviews = useMemo(() => {
    const reviewers = [
      "James Turner",
      "Olivia White",
      "Michael Carter",
      "Emma Johnson",
      "David Miller",
    ];
    const comments = [
      "Excellent print quality and very easy to set up. Highly recommended.",
      "Perfect for my home office. Quiet, fast, and reliable.",
      "Delivery was quick and the printer works better than expected.",
      "Value for money. The prints are sharp and colors are accurate.",
      "This integrates well with my devices and wireless works flawlessly.",
    ];
    return Array.from({ length: 4 }, (_, i) => ({
      name: reviewers[i],
      text: comments[i],
      rating: Math.floor(Math.random() * 2) + 4, // 4 or 5
      date: `January ${12 + i}, 2025`,
    }));
  }, []);

  // 🔹 Image resolver (handles base64 / array)
  const getImage = () => {
    if (!product) return "/assets/img/placeholder.png";

    if (Array.isArray(product.imageUrl) && product.imageUrl.length > 0) {
      return product.imageUrl[0];
    }

    if (typeof product.imageUrl === "string" && product.imageUrl.trim()) {
      return product.imageUrl;
    }

    return "/assets/img/placeholder.png";
  };

  // 🔹 Add to cart with session check
  const handleAddToCart = () => {
    if (!product) return;

    const isLoggedIn = sessionStorage.getItem("isLoggedIn");
    if (isLoggedIn !== "true") {
      toast.error("Please login first!");
      navigate("/login-now");
      return;
    }

    let cart = JSON.parse(sessionStorage.getItem("cart")) || [];
    const existing = cart.find((item) => item.id === product.id);

    if (existing) {
      existing.quantity += quantity;
    } else {
      cart.push({ ...product, quantity });
    }

    sessionStorage.setItem("cart", JSON.stringify(cart));
    toast.success(`${product.name} added to cart`);
  };

  if (!product) {
    return <div className="text-center py-5">Product not found!</div>;
  }

  const displayDescription =
    product.description ||
    "This product is currently missing a detailed description.";
  const displayAdditional =
    product.specifications ||
    product.compatibility ||
    "Additional technical specifications will be updated soon.";

  const avgRating = (Math.random() * 0.5 + 4.3).toFixed(1); // ~4.3–4.8
  const totalReviews = randomReviews.length + 20; // random-ish count

  return (
    <section className="product-details space-top space-extra-bottom">
      <div className="container">
        <div className="row gx-60">
          {/* LEFT: IMAGE */}
          <div className="col-lg-6">
            <div className="product-big-img">
              <div className="img">
                <img src={getImage()} alt={product.name} />
              </div>
            </div>
          </div>

          {/* RIGHT: DETAILS */}
          <div className="col-lg-6 align-self-center">
            <div className="product-about">
              <p className="price">${product.price}</p>
              <h2 className="product-title">{product.name}</h2>

              <div className="product-rating">
                <div
                  className="star-rating"
                  role="img"
                  aria-label={`Rated ${avgRating} out of 5`}
                >
                  <span style={{ width: "100%" }}>
                    Rated <strong className="rating">{avgRating}</strong> out of
                    5 based on <span className="rating">{totalReviews}</span>{" "}
                    customer reviews
                  </span>
                </div>
                <span className="woocommerce-review-link">
                  (<span className="count">{totalReviews}</span> reviews)
                </span>
              </div>

              <p className="text">{displayDescription}</p>

              <div className="mt-2 link-inherit">
                <p>
                  <strong className="text-title me-3">Availability:</strong>
                  <span className="stock in-stock">
                    <i className="far fa-check-square me-2 ms-1" />
                    In Stock
                  </span>
                </p>
              </div>

              {/* QUANTITY + ADD TO CART (no wishlist) */}
              <div className="actions">
                <div className="quantity">
                  <button
                    type="button"
                    className="quantity-minus qty-btn"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  >
                    <i className="far fa-chevron-down" />
                  </button>
                  <input
                    type="number"
                    className="qty-input"
                    min={1}
                    value={quantity}
                    readOnly
                  />
                  <button
                    type="button"
                    className="quantity-plus qty-btn"
                    onClick={() => setQuantity((q) => q + 1)}
                  >
                    <i className="far fa-chevron-up" />
                  </button>
                </div>

                <button className="th-btn" onClick={handleAddToCart}>
                  Add to Cart
                </button>
              </div>

              {/* META INFO FROM PRODUCT DATA */}
              <div className="product_meta">
                {product.brand && (
                  <span className="sku_wrapper d-block">
                    Brand: <span className="sku">{product.brand}</span>
                  </span>
                )}

                {product.category && (
                  <span className="posted_in d-block">
                    Category:{" "}
                    <Link to="/shop">
                      {product.category.replace(/-/g, " ")}
                    </Link>
                  </span>
                )}

                {Array.isArray(product.categories) &&
                  product.categories.length > 0 && (
                    <span className="d-block">
                      Tags:{" "}
                      {product.categories.map((c, i) => (
                        <Link key={i} to="/shop">
                          {c.replace(/-/g, " ")}
                        </Link>
                      ))}
                    </span>
                  )}
              </div>
            </div>
          </div>
        </div>

        {/* TABS */}
        <ul className="nav product-tab-style2" id="productTab" role="tablist">
          <li className="nav-item" role="presentation">
            <a
              id="description-tab"
              data-bs-toggle="tab"
              data-bs-target="#description"
              role="tab"
              aria-controls="description"
              aria-selected={activeTab === "tab-desc"}
              className={`nav-link ${activeTab === "tab-desc" ? "active" : ""}`}
              type="button"
              onClick={() => setActiveTab("tab-desc")}
            >
              Description
            </a>
          </li>
          <li className="nav-item" role="presentation">
            <a
              id="additional-tab"
              data-bs-toggle="tab"
              data-bs-target="#additional"
              role="tab"
              aria-controls="additional"
              aria-selected={activeTab === "tab-additional"}
              className={`nav-link ${
                activeTab === "tab-additional" ? "active" : ""
              }`}
              type="button"
              onClick={() => setActiveTab("tab-additional")}
            >
              Additional Information
            </a>
          </li>
          <li className="nav-item" role="presentation">
            <a
              id="reviews-tab"
              data-bs-toggle="tab"
              data-bs-target="#reviews"
              role="tab"
              aria-controls="reviews"
              aria-selected={activeTab === "tab-reviews"}
              className={`nav-link ${
                activeTab === "tab-reviews" ? "active" : ""
              }`}
              type="button"
              onClick={() => setActiveTab("tab-reviews")}
            >
              Customer Reviews
            </a>
          </li>
        </ul>

        {/* TAB CONTENT */}
        <div className="tab-content" id="productTabContent">
          {/* DESCRIPTION TAB */}
          <div
            className={`tab-pane fade ${
              activeTab === "tab-desc" ? "show active" : ""
            }`}
            id="description"
            role="tabpanel"
            aria-labelledby="description-tab"
          >
            <p>{displayDescription}</p>
          </div>

          {/* ADDITIONAL INFO TAB */}
          <div
            className={`tab-pane fade ${
              activeTab === "tab-additional" ? "show active" : ""
            }`}
            id="additional"
            role="tabpanel"
            aria-labelledby="additional-tab"
          >
            <p style={{ whiteSpace: "pre-line" }}>{displayAdditional}</p>
          </div>

          {/* REVIEWS TAB – ONLY STATIC REVIEWS, NO FORM */}
          <div
            className={`tab-pane fade ${
              activeTab === "tab-reviews" ? "show active" : ""
            }`}
            id="reviews"
            role="tabpanel"
            aria-labelledby="reviews-tab"
          >
            <div className="woocommerce-Reviews">
              <div className="th-comments-wrap">
                <ul className="comment-list">
                  {randomReviews.map((rev, i) => (
                    <li key={i} className="review th-comment-item">
                      <div className="th-post-comment">
                        <div className="comment-avater">
                          <div className="avatar-circle">
                            {rev.name.charAt(0)}
                          </div>
                        </div>
                        <div className="comment-content">
                          <h4 className="name">{rev.name}</h4>
                          <span className="commented-on">
                            <i className="far fa-calendar" /> {rev.date}
                          </span>
                          <div
                            className="star-rating"
                            role="img"
                            aria-label={`Rated ${rev.rating} out of 5`}
                          >
                            <span style={{ width: "100%" }}>
                              {"⭐".repeat(rev.rating)}
                            </span>
                          </div>
                          <p className="text">{rev.text}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Review form removed as requested */}
            </div>
          </div>
        </div>
      </div>

      {/* Small CSS helper for avatar circle if not present in theme */}
      <style>{`
        .avatar-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #0b61d6;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
        }
      `}</style>
    </section>
  );
}

export default ShopDetailsMain;
